import { cookieUtils } from './cookies.js';
import { authAPI } from '../services/api.js';
import Swal from 'sweetalert2';

// In-memory cache to avoid persisting user details in localStorage
let cachedUser = null;

const enumRoleMap = {
    1: 'Admin',
    2: 'Student',
    3: 'Instructor'
};

const roleToEnumMap = {
    Admin: 1,
    Student: 2,
    Instructor: 3
};

const normalizeRoleName = (value) => {
    if (!value && value !== 0) return undefined;
    const str = String(value).trim();
    // Map synonyms
    if (/^teacher$/i.test(str)) return 'Instructor';
    if (/^instructor$/i.test(str)) return 'Instructor';
    if (/^admin$/i.test(str)) return 'Admin';
    if (/^student$/i.test(str)) return 'Student';
    return str;
};

export const authUtils = {
    // Check if user is authenticated (with token expiry validation)
    isAuthenticated: () => {
        const token = cookieUtils.getAccessToken();
        if (!token) {
            return false;
        }

        try {
            // Decode JWT token to check expiry
            const payload = JSON.parse(atob(token.split('.')[1]));

            // Check if token has expired
            if (payload.exp && payload.exp * 1000 < Date.now()) {
                // Token expired, clear it and trigger auth state change
                cookieUtils.clearAuthCookies();
                cachedUser = null;
                window.dispatchEvent(new Event('auth:stateChanged'));
                return false;
            }

            return true;
        } catch (error) {
            // Invalid token format, clear it and trigger auth state change
            console.error('Invalid token format:', error);
            cookieUtils.clearAuthCookies();
            cachedUser = null;
            window.dispatchEvent(new Event('auth:stateChanged'));
            return false;
        }
    },

    // Get current user
    getCurrentUser: () => {
        const tokenData = authUtils.decodeToken();
        if (!tokenData) {
            return cachedUser;
        }

        const roleName = tokenData.role || tokenData.userType || '';
        const roleValue = roleToEnumMap[roleName] ?? undefined;

        const fullName = (tokenData.fullName || '').trim();
        const [firstName = '', ...rest] = fullName.split(' ').filter(Boolean);
        const lastName = rest.join(' ');

        const user = {
            id: tokenData.userId,
            email: tokenData.email,
            role: roleName,
            userType: roleName,
            userTypeName: roleName,
            userTypeValue: roleValue,
            fullName,
            firstName,
            lastName
        };

        cachedUser = user;
        return user;
    },

    // Decode JWT token to get user ID and other claims
    decodeToken: () => {
        const token = cookieUtils.getAccessToken();
        if (!token) {
            return null;
        }
        
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));

            // Extract role from multiple possible claim keys
            const rawRole = payload.role 
                || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
                || payload['roles']
                || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/roles'];

            // Normalize role: handle number, array, string, trim and case
            let normalizedRole = undefined;
            if (Array.isArray(rawRole)) {
                normalizedRole = normalizeRoleName(rawRole[0]);
            } else if (typeof rawRole === 'number') {
                normalizedRole = enumRoleMap[rawRole];
            } else {
                normalizedRole = normalizeRoleName(rawRole);
            }

            // Extract userType from multiple possible claim keys
            const rawUserType = payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/usertype'] 
                || payload.user_type 
                || payload.userType;

            let normalizedUserType = undefined;
            if (typeof rawUserType === 'number') {
                normalizedUserType = enumRoleMap[rawUserType];
            } else {
                normalizedUserType = normalizeRoleName(rawUserType);
            }

            const decodedData = {
                userId: payload.sub || payload.nameid || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
                email: payload.email || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
                role: normalizedRole,
                fullName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || `${payload.given_name || ''} ${payload.family_name || ''}`.trim(),
                userType: normalizedUserType
            };
            
            return decodedData;
        } catch (error) {
            console.error('Error decoding token:', error);
            console.error('Token:', token);
            return null;
        }
    },

    // Logout function - now handled by AuthContext
    logout: async () => {
        // This method is deprecated - logout is now handled by AuthContext
        console.warn('authUtils.logout() is deprecated. Use useAuth().logout() instead.');
    },

    // Get auth headers for API calls
    getAuthHeaders: () => {
        const token = cookieUtils.getAccessToken();
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    },

    // Cache user details in-memory only (no persistence)
    setCachedUser: (userData) => {
        cachedUser = userData ? { ...userData } : null;
    },

    clearCachedUser: () => {
        cachedUser = null;
    }
};